/**
 * Modal — one accessible dialog for the whole app.
 *
 * There were four ad-hoc modal implementations here, each a plain `<div>` over a
 * dimmed backdrop: no dialog role, no focus trap, no Escape handling, and focus
 * left sitting on whatever button opened them. Screen readers announced nothing
 * and keyboard users could Tab straight out into the page behind.
 *
 * This component owns that behaviour once:
 *   - `role="dialog"` + `aria-modal` + `aria-labelledby` on the rendered title
 *   - focus moves in on open, is trapped while open, and is restored on close
 *   - Escape and overlay clicks close (unless `dismissable` is false)
 *   - `document.body` scrolling is locked while open and restored afterwards
 */

import { useCallback, useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'

/** Elements that can hold focus, used to find the trap boundaries. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer = null,
  labelledBy,
  maxWidth = '540px',
  /** When false, Escape and overlay clicks are ignored and no close button renders. */
  dismissable = true,
}) {
  const dialogRef = useRef(null)
  const previouslyFocusedRef = useRef(null)
  const generatedId = useId()
  const titleId = labelledBy || `${generatedId}-title`

  const requestClose = useCallback(() => {
    if (!dismissable) return
    onClose?.()
  }, [dismissable, onClose])

  // Remember what had focus, then move focus into the dialog.
  useEffect(() => {
    if (!isOpen) return

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const node = dialogRef.current
    if (node) {
      const first = node.querySelector(FOCUSABLE)
      // Falls back to the dialog itself, which carries tabIndex={-1}.
      ;(first instanceof HTMLElement ? first : node).focus()
    }

    const restoreTo = previouslyFocusedRef.current
    return () => {
      // Guard: the trigger may have unmounted while the dialog was open.
      if (restoreTo && document.contains(restoreTo)) restoreTo.focus()
    }
  }, [isOpen])

  // Lock background scrolling, restoring whatever was there before.
  useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [isOpen])

  // Escape to close, Tab/Shift+Tab trapped inside.
  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!dismissable) return
        event.stopPropagation()
        requestClose()
        return
      }

      if (event.key !== 'Tab') return

      const node = dialogRef.current
      if (!node) return

      const focusable = Array.from(node.querySelectorAll(FOCUSABLE)).filter(
        (el) => el instanceof HTMLElement && el.offsetParent !== null,
      )

      if (focusable.length === 0) {
        // Nothing to move to; keep focus on the dialog rather than escaping it.
        event.preventDefault()
        node.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || active === node)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      } else if (!node.contains(active)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [isOpen, dismissable, requestClose])

  if (!isOpen) return null

  return (
    <div
      className="modal-overlay"
      // Overlay clicks close; clicks that originate inside the panel do not,
      // because the check is against the overlay itself as the event target.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose()
      }}
    >
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ maxWidth }}
      >
        <div className="modal-header">
          <h2 id={titleId}>{title}</h2>
          {dismissable && (
            <button type="button" className="btn-icon" aria-label="Close dialog" onClick={requestClose}>
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="modal-body">{children}</div>

        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  )
}
